import { useState, useEffect, useMemo } from 'react';
import { Check, X, Send, CheckSquare, Square, Search, Eye, Mail } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import './CampaignManager.scss';

const CampaignManager = () => {
    const { success, error, warning, confirm } = useSnackbar();
    const [availabilitySending, setAvailabilitySending] = useState(false);
    const [availabilitySendingProgress, setAvailabilitySendingProgress] = useState(null);
    const [availabilityResult, setAvailabilityResult] = useState(null);
    const [showAvailabilityPreview, setShowAvailabilityPreview] = useState(false);
    const [availabilityRecipients, setAvailabilityRecipients] = useState([]);
    const [selectedAvailabilityEmails, setSelectedAvailabilityEmails] = useState([]);
    const [availabilitySearchTerm, setAvailabilitySearchTerm] = useState('');
    const [availabilityPreviewLoading, setAvailabilityPreviewLoading] = useState(false);
    const [contributors, setContributors] = useState([]);
    const [commenters, setCommenters] = useState([]);
    const [recipientType, setRecipientType] = useState('both'); // 'contributors', 'commenters', 'both'
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [sendingProgress, setSendingProgress] = useState(null);
    const [campaignData, setCampaignData] = useState({
        subject: '',
        htmlContent: ''
    });
    const [result, setResult] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const lastYear = new Date().getFullYear() - 1;

    const fetchData = async () => {
        setLoading(true);
        try {
            const [contributorsRes, commentersRes] = await Promise.all([
                api.get('/admin/contributors', { params: { year: lastYear } }),
                api.get('/admin/commenters', { params: { year: lastYear } })
            ]);
            
            if (contributorsRes.data.success) {
                setContributors(contributorsRes.data.contributors);
            }
            if (commentersRes.data.success) {
                setCommenters(commentersRes.data.commenters);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get current recipient list based on type
    const currentRecipients = useMemo(() => {
        if (recipientType === 'contributors') {
            return contributors;
        } else if (recipientType === 'commenters') {
            return commenters;
        } else {
            // Combine both and remove duplicates by email
            const combined = [...contributors, ...commenters];
            const unique = combined.reduce((acc, item) => {
                if (!acc.find(i => i.email === item.email)) {
                    acc.push(item);
                }
                return acc;
            }, []);
            return unique;
        }
    }, [contributors, commenters, recipientType]);

    // Calculate unique count for "Both" tab
    const bothCount = useMemo(() => {
        const allEmails = new Set([
            ...contributors.map(c => c.email),
            ...commenters.map(c => c.email)
        ].filter(email => email != null));
        return allEmails.size;
    }, [contributors, commenters]);

    // Filter recipients based on search term
    const filteredRecipients = useMemo(() => {
        if (!searchTerm.trim()) {
            return currentRecipients;
        }
        const search = searchTerm.toLowerCase();
        return currentRecipients.filter(recipient =>
            (recipient.name && recipient.name.toLowerCase().includes(search)) ||
            (recipient.email && recipient.email.toLowerCase().includes(search))
        );
    }, [currentRecipients, searchTerm]);

    const toggleSelectAll = () => {
        const filteredEmails = filteredRecipients
            .map(r => r.email)
            .filter(email => email != null); // Filter out null/undefined emails
        const allFilteredSelected = filteredEmails.length > 0 && filteredEmails.every(email => selectedEmails.includes(email));
        
        if (allFilteredSelected) {
            // Deselect all filtered recipients
            setSelectedEmails(selectedEmails.filter(email => !filteredEmails.includes(email)));
        } else {
            // Select all filtered recipients
            const newSelected = [...new Set([...selectedEmails, ...filteredEmails])];
            setSelectedEmails(newSelected);
        }
    };

    const toggleSelect = (email) => {
        if (selectedEmails.includes(email)) {
            setSelectedEmails(selectedEmails.filter(e => e !== email));
        } else {
            setSelectedEmails([...selectedEmails, email]);
        }
    };

    const handlePreviewAvailabilityCampaign = async () => {
        setAvailabilityPreviewLoading(true);
        setShowAvailabilityPreview(true);
        setAvailabilityRecipients([]);
        setSelectedAvailabilityEmails([]);
        setAvailabilitySearchTerm('');
        try {
            const res = await api.get('/admin/contributors', { params: { year: 2025 } });
            if (res.data.success && res.data.contributors) {
                const recipients = res.data.contributors.filter(c => c.email);
                setAvailabilityRecipients(recipients);
                setSelectedAvailabilityEmails(recipients.map(c => c.email));
            } else {
                error('Could not load recipients');
            }
        } catch (err) {
            error(err.response?.data?.message || err.message || 'Could not load recipients');
        } finally {
            setAvailabilityPreviewLoading(false);
        }
    };

    const filteredAvailabilityRecipients = useMemo(() => {
        if (!availabilitySearchTerm.trim()) return availabilityRecipients;
        const search = availabilitySearchTerm.toLowerCase();
        return availabilityRecipients.filter(c =>
            (c.name && c.name.toLowerCase().includes(search)) ||
            (c.email && c.email.toLowerCase().includes(search))
        );
    }, [availabilityRecipients, availabilitySearchTerm]);

    const toggleAvailabilitySelectAll = () => {
        const allFilteredSelected = filteredAvailabilityRecipients.length > 0 &&
            filteredAvailabilityRecipients.every(c => selectedAvailabilityEmails.includes(c.email));
        if (allFilteredSelected) {
            setSelectedAvailabilityEmails(prev =>
                prev.filter(e => !filteredAvailabilityRecipients.some(c => c.email === e))
            );
        } else {
            const toAdd = filteredAvailabilityRecipients.map(c => c.email).filter(e => !selectedAvailabilityEmails.includes(e));
            setSelectedAvailabilityEmails(prev => [...prev, ...toAdd]);
        }
    };

    const toggleAvailabilitySelect = (email) => {
        setSelectedAvailabilityEmails(prev =>
            prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
        );
    };

    const handleConfirmSendAvailabilityCampaign = () => {
        if (selectedAvailabilityEmails.length === 0) {
            warning('Please select at least one recipient');
            return;
        }
        confirm(
            `Send campaign to ${selectedAvailabilityEmails.length} selected contributor${selectedAvailabilityEmails.length !== 1 ? 's' : ''}?`,
            async () => {
                setShowAvailabilityPreview(false);
                setAvailabilitySending(true);
                setAvailabilityResult(null);
                setAvailabilitySendingProgress(null);

                const total = selectedAvailabilityEmails.length;
                let successCount = 0;
                let errorCount = 0;
                const errors = [];

                for (let i = 0; i < selectedAvailabilityEmails.length; i++) {
                    const email = selectedAvailabilityEmails[i];

                    setAvailabilitySendingProgress({
                        current: i + 1,
                        total,
                        currentEmail: email,
                        successCount,
                        errorCount,
                        errors: [...errors],
                        completed: false
                    });

                    try {
                        await api.post('/admin/contributor-availability-campaign/send-single', { email });
                        successCount++;
                    } catch (err) {
                        const errorMessage = err.response?.data?.message || err.message || 'Failed to send';
                        errorCount++;
                        errors.push({ email, error: errorMessage });
                    }

                    setAvailabilitySendingProgress({
                        current: i + 1,
                        total,
                        currentEmail: email,
                        successCount,
                        errorCount,
                        errors: [...errors],
                        completed: i === selectedAvailabilityEmails.length - 1
                    });

                    if (i < selectedAvailabilityEmails.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                setAvailabilityResult({
                    success: true,
                    total,
                    sent: successCount,
                    failed: errorCount,
                    errors: errors.length > 0 ? errors : undefined
                });
                success(`Campaign sent: ${successCount} succeeded, ${errorCount} failed`);
                setAvailabilitySending(false);
            }
        );
    };

    const handleSend = async () => {
        if (!campaignData.subject || !campaignData.htmlContent) {
            warning('Please fill in subject and content');
            return;
        }

        if (selectedEmails.length === 0) {
            warning('Please select at least one contributor');
            return;
        }

        confirm(
            `Send email to ${selectedEmails.length} contributor${selectedEmails.length > 1 ? 's' : ''}?`,
            async () => {
                setSending(true);
                setResult(null);
                
                // Initialize progress tracking
                const total = selectedEmails.length;
                const progress = {
                    current: 0,
                    total: total,
                    currentEmail: '',
                    successCount: 0,
                    errorCount: 0,
                    errors: []
                };
                setSendingProgress(progress);

                // Send emails one by one to show progress
                let successCount = 0;
                let errorCount = 0;
                const errors = [];

                for (let i = 0; i < selectedEmails.length; i++) {
                    const email = selectedEmails[i];
                    
                    // Update progress
                    setSendingProgress({
                        current: i + 1,
                        total: total,
                        currentEmail: email,
                        successCount: successCount,
                        errorCount: errorCount,
                        errors: [...errors]
                    });

                    try {
                        await api.post('/admin/campaigns/send-single', {
                            subject: campaignData.subject,
                            htmlContent: campaignData.htmlContent,
                            email: email
                        });
                        
                        successCount++;
                    } catch (err) {
                        const errorMessage = err.response?.data?.message || err.message || 'Failed to send';
                        errorCount++;
                        errors.push({ email, error: errorMessage });
                    }
                    
                    // Update progress after each email
                    setSendingProgress({
                        current: i + 1,
                        total: total,
                        currentEmail: email,
                        successCount: successCount,
                        errorCount: errorCount,
                        errors: [...errors]
                    });
                    
                    // Small delay to prevent overwhelming the server
                    if (i < selectedEmails.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                // Final summary
                const finalProgress = {
                    current: total,
                    total: total,
                    currentEmail: '',
                    successCount: successCount,
                    errorCount: errorCount,
                    errors: errors,
                    completed: true
                };
                
                // Update progress to show completion
                setSendingProgress(finalProgress);
                
                setResult({
                    success: true,
                    summary: {
                        total: total,
                        successCount: finalProgress.successCount,
                        errorCount: finalProgress.errorCount
                    },
                    errors: finalProgress.errors.length > 0 ? finalProgress.errors : undefined
                });
                
                setSending(false);
            }
        );
    };

    return (
        <div className="campaign-manager">
            <h2>Email Campaign Manager</h2>

            <div className="campaign-availability-section">
                <h3>2025 Contributor Availability Campaign</h3>
                <p>Send emails to all 2025 contributors with their mosque list. Each mosque has Yes available / Not available / Not sure buttons. Clicking &quot;Yes available&quot; copies the mosque to 2026.</p>
                <button
                    className="availability-campaign-button"
                    onClick={handlePreviewAvailabilityCampaign}
                    disabled={availabilitySending}
                >
                    <Mail size={18} />
                    {availabilitySending ? 'Sending...' : 'Preview & Send 2025 Contributor Availability Campaign'}
                </button>
            </div>

            {availabilityResult && (
                <div className={`result-panel ${availabilityResult.success ? 'success' : 'error'}`}>
                    {availabilityResult.success ? (
                        <>
                            <h3>Availability Campaign Sent Successfully!</h3>
                            <div className="result-stats">
                                <div>Total: {availabilityResult.total}</div>
                                <div>Success: {availabilityResult.sent}</div>
                                <div>Failed: {availabilityResult.failed}</div>
                            </div>
                            {availabilityResult.errors?.length > 0 && (
                                <div className="errors-list">
                                    <strong>Errors:</strong>
                                    {availabilityResult.errors.map((err, idx) => (
                                        <div key={idx}>{err.email}: {err.error}</div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="error-message">{availabilityResult.message || 'Failed to send campaign'}</div>
                    )}
                    <button onClick={() => setAvailabilityResult(null)}>Close</button>
                </div>
            )}

            <p className="campaign-year-notice">Showing {lastYear} contributors & commenters only</p>

            <div className="campaign-content">
                <div className="campaign-form">
                    <div className="form-group">
                        <label>Email Subject *</label>
                        <input
                            type="text"
                            value={campaignData.subject}
                            onChange={(e) => setCampaignData({ ...campaignData, subject: e.target.value })}
                            placeholder="Enter email subject"
                        />
                    </div>

                    <div className="form-group">
                        <div className="form-group-header">
                            <label>HTML Content *</label>
                            <button
                                type="button"
                                className="preview-button"
                                onClick={() => setShowPreview(true)}
                                disabled={!campaignData.htmlContent.trim()}
                            >
                                <Eye size={16} />
                                Preview
                            </button>
                        </div>
                        <textarea
                            value={campaignData.htmlContent}
                            onChange={(e) => setCampaignData({ ...campaignData, htmlContent: e.target.value })}
                            placeholder="Enter HTML email content"
                            rows="15"
                        />
                        <small>You can use HTML tags for formatting</small>
                    </div>

                    <button
                        className="send-button"
                        onClick={handleSend}
                        disabled={sending || selectedEmails.length === 0}
                    >
                        <Send size={18} />
                        {sending ? 'Sending...' : `Send to ${selectedEmails.length} Recipient${selectedEmails.length !== 1 ? 's' : ''}`}
                    </button>
                </div>

                <div className="contributors-panel">
                    <div className="panel-header">
                        <h3>Select Recipients ({selectedEmails.length} selected)</h3>
                        <button className="select-all-button" onClick={toggleSelectAll}>
                            {filteredRecipients.length > 0 && filteredRecipients
                                .filter(r => r.email != null)
                                .every(r => selectedEmails.includes(r.email)) ? (
                                <>
                                    <CheckSquare size={18} />
                                    Deselect All
                                </>
                            ) : (
                                <>
                                    <Square size={18} />
                                    Select All
                                </>
                            )}
                        </button>
                    </div>

                    <div className="recipient-type-selector">
                        <button
                            className={`type-tab ${recipientType === 'contributors' ? 'active' : ''}`}
                            onClick={() => setRecipientType('contributors')}
                        >
                            Contributors ({contributors.length})
                        </button>
                        <button
                            className={`type-tab ${recipientType === 'commenters' ? 'active' : ''}`}
                            onClick={() => setRecipientType('commenters')}
                        >
                            Commenters ({commenters.length})
                        </button>
                        <button
                            className={`type-tab ${recipientType === 'both' ? 'active' : ''}`}
                            onClick={() => setRecipientType('both')}
                        >
                            Both ({bothCount})
                        </button>
                    </div>

                    <div className="search-box">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search recipients by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="clear-search"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="loading">Loading recipients...</div>
                    ) : (
                        <>
                            {filteredRecipients.length === 0 ? (
                                <div className="no-results">
                                    <p>No recipients found matching "{searchTerm}"</p>
                                </div>
                            ) : (
                                <div className="contributors-list">
                                    {filteredRecipients.map((recipient) => {
                                        const isContributor = contributors.some(c => c.email === recipient.email);
                                        const isCommenter = commenters.some(c => c.email === recipient.email);
                                        const recipientTypeLabel = isContributor && isCommenter 
                                            ? 'Contributor & Commenter' 
                                            : isContributor 
                                                ? 'Contributor' 
                                                : 'Commenter';
                                        
                                        return (
                                            <div
                                                key={recipient.email || Math.random()}
                                                className={`contributor-item ${recipient.email && selectedEmails.includes(recipient.email) ? 'selected' : ''}`}
                                                onClick={() => recipient.email && toggleSelect(recipient.email)}
                                            >
                                                <div className="checkbox">
                                                    {selectedEmails.includes(recipient.email) ? (
                                                        <Check size={18} />
                                                    ) : (
                                                        <Square size={18} />
                                                    )}
                                                </div>
                                                <div className="contributor-info">
                                                    <div className="recipient-header">
                                                        <strong>{recipient.name || 'Unknown'}</strong>
                                                        <span className="recipient-type-badge">{recipientTypeLabel}</span>
                                                    </div>
                                                    <span>{recipient.email || 'No email'}</span>
                                                    <small>
                                                        {recipient.totalMosques ? `${recipient.totalMosques} mosques` : ''}
                                                        {recipient.totalComments ? `${recipient.totalComments} comments` : ''}
                                                    </small>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {searchTerm && (
                                <div className="search-info">
                                    Showing {filteredRecipients.length} of {currentRecipients.length} recipients
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {result && (
                <div className={`result-panel ${result.success ? 'success' : 'error'}`}>
                    {result.success ? (
                        <>
                            <h3>Campaign Sent Successfully!</h3>
                            <div className="result-stats">
                                <div>Total: {result.summary.total}</div>
                                <div>Success: {result.summary.successCount}</div>
                                <div>Failed: {result.summary.errorCount}</div>
                            </div>
                            {result.errors && result.errors.length > 0 && (
                                <div className="errors-list">
                                    <strong>Errors:</strong>
                                    {result.errors.map((err, idx) => (
                                        <div key={idx}>{err.email}: {err.error}</div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="error-message">{result.message}</div>
                    )}
                    <button onClick={() => setResult(null)}>Close</button>
                </div>
            )}

            {availabilitySendingProgress && (
                <div className="progress-overlay">
                    <div className="progress-modal">
                        <div className="progress-header">
                            <h3>
                                {availabilitySendingProgress.completed
                                    ? 'Availability Campaign Completed'
                                    : 'Sending Availability Campaign'}
                            </h3>
                            <button
                                className="progress-close"
                                onClick={() => {
                                    setAvailabilitySendingProgress(null);
                                    if (availabilitySendingProgress.completed) {
                                        setAvailabilitySending(false);
                                    }
                                }}
                                aria-label="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="progress-content">
                            <div className="progress-stats">
                                <div className="progress-stat">
                                    <span className="stat-label">Progress:</span>
                                    <span className="stat-value">
                                        {availabilitySendingProgress.current} / {availabilitySendingProgress.total}
                                    </span>
                                </div>
                                <div className="progress-stat">
                                    <span className="stat-label">Success:</span>
                                    <span className="stat-value success">{availabilitySendingProgress.successCount}</span>
                                </div>
                                <div className="progress-stat">
                                    <span className="stat-label">Failed:</span>
                                    <span className="stat-value error">{availabilitySendingProgress.errorCount}</span>
                                </div>
                            </div>
                            <div className="progress-bar-container">
                                <div
                                    className={`progress-bar ${availabilitySendingProgress.completed ? 'completed' : ''}`}
                                    style={{ width: `${(availabilitySendingProgress.current / availabilitySendingProgress.total) * 100}%` }}
                                />
                            </div>
                            {availabilitySendingProgress.completed ? (
                                <div className="completion-message">
                                    {availabilitySendingProgress.errorCount === 0 ? (
                                        <div className="completion-success">✓ All emails sent successfully!</div>
                                    ) : (
                                        <div className="completion-warning">
                                            Campaign completed with {availabilitySendingProgress.errorCount} error{availabilitySendingProgress.errorCount > 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                            ) : availabilitySendingProgress.currentEmail && (
                                <div className="current-email">
                                    <span className="current-label">Sending to:</span>
                                    <span className="current-email-value">{availabilitySendingProgress.currentEmail}</span>
                                </div>
                            )}
                            {availabilitySendingProgress.errors?.length > 0 && (
                                <div className="progress-errors">
                                    <strong>Errors:</strong>
                                    <div className="errors-list">
                                        {availabilitySendingProgress.errors.map((err, idx) => (
                                            <div key={idx} className="error-item">
                                                <span className="error-email">{err.email}:</span>
                                                <span className="error-message">{err.error}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showAvailabilityPreview && (
                <div className="preview-overlay" onClick={() => setShowAvailabilityPreview(false)}>
                    <div className="availability-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="preview-header">
                            <h3>2025 Contributor Availability Campaign – Recipients</h3>
                            <button
                                className="preview-close"
                                onClick={() => setShowAvailabilityPreview(false)}
                                aria-label="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="availability-preview-content">
                            {availabilityPreviewLoading ? (
                                <div className="loading">Loading recipients...</div>
                            ) : availabilityRecipients.length === 0 ? (
                                <p className="no-recipients">No 2025 contributors with email found.</p>
                            ) : (
                                <>
                                    <div className="availability-panel-header">
                                        <p className="recipients-summary">
                                            <strong>{selectedAvailabilityEmails.length}</strong> of {availabilityRecipients.length} selected
                                        </p>
                                        <button
                                            className="select-all-availability"
                                            onClick={toggleAvailabilitySelectAll}
                                        >
                                            {filteredAvailabilityRecipients.length > 0 &&
                                            filteredAvailabilityRecipients.every(c => selectedAvailabilityEmails.includes(c.email)) ? (
                                                <><CheckSquare size={16} /> Deselect All</>
                                            ) : (
                                                <><Square size={16} /> Select All</>
                                            )}
                                        </button>
                                    </div>
                                    <div className="availability-search-box">
                                        <Search size={18} />
                                        <input
                                            type="text"
                                            placeholder="Search by name or email..."
                                            value={availabilitySearchTerm}
                                            onChange={(e) => setAvailabilitySearchTerm(e.target.value)}
                                        />
                                        {availabilitySearchTerm && (
                                            <button
                                                className="clear-search"
                                                onClick={() => setAvailabilitySearchTerm('')}
                                                title="Clear search"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="availability-recipients-list">
                                        {filteredAvailabilityRecipients.map((c) => (
                                            <div
                                                key={c.email}
                                                className={`availability-recipient-item ${selectedAvailabilityEmails.includes(c.email) ? 'selected' : ''}`}
                                                onClick={() => toggleAvailabilitySelect(c.email)}
                                            >
                                                <div className="checkbox">
                                                    {selectedAvailabilityEmails.includes(c.email) ? (
                                                        <Check size={18} />
                                                    ) : (
                                                        <Square size={18} />
                                                    )}
                                                </div>
                                                <div className="recipient-info">
                                                    <strong>{c.name || 'Unknown'}</strong>
                                                    <span>{c.email}</span>
                                                    {c.totalMosques && (
                                                        <small>{c.totalMosques} mosque{c.totalMosques !== 1 ? 's' : ''}</small>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {availabilitySearchTerm && (
                                        <p className="availability-search-info">
                                            Showing {filteredAvailabilityRecipients.length} of {availabilityRecipients.length}
                                        </p>
                                    )}
                                    <div className="availability-preview-actions">
                                        <button
                                            className="cancel-button"
                                            onClick={() => setShowAvailabilityPreview(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="send-button"
                                            onClick={handleConfirmSendAvailabilityCampaign}
                                            disabled={selectedAvailabilityEmails.length === 0}
                                        >
                                            <Send size={18} />
                                            Send to {selectedAvailabilityEmails.length} Selected
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showPreview && (
                <div className="preview-overlay" onClick={() => setShowPreview(false)}>
                    <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="preview-header">
                            <h3>Email Preview</h3>
                            <button
                                className="preview-close"
                                onClick={() => setShowPreview(false)}
                                aria-label="Close preview"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="preview-content">
                            <div className="preview-subject">
                                <strong>Subject:</strong> {campaignData.subject || '(No subject)'}
                            </div>
                            <div className="preview-html" dangerouslySetInnerHTML={{ __html: campaignData.htmlContent || '<p>No content</p>' }} />
                        </div>
                    </div>
                </div>
            )}

            {sendingProgress && (
                <div className="progress-overlay">
                    <div className="progress-modal">
                        <div className="progress-header">
                            <h3>
                                {sendingProgress.completed 
                                    ? 'Campaign Sending Completed' 
                                    : 'Sending Campaign Emails'}
                            </h3>
                            <button
                                className="progress-close"
                                onClick={() => {
                                    setSendingProgress(null);
                                    if (sendingProgress.completed) {
                                        setCampaignData({ subject: '', htmlContent: '' });
                                        setSelectedEmails([]);
                                    }
                                }}
                                aria-label="Close"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="progress-content">
                            <div className="progress-stats">
                                <div className="progress-stat">
                                    <span className="stat-label">Progress:</span>
                                    <span className="stat-value">
                                        {sendingProgress.current} / {sendingProgress.total}
                                    </span>
                                </div>
                                <div className="progress-stat">
                                    <span className="stat-label">Success:</span>
                                    <span className="stat-value success">{sendingProgress.successCount}</span>
                                </div>
                                <div className="progress-stat">
                                    <span className="stat-label">Failed:</span>
                                    <span className="stat-value error">{sendingProgress.errorCount}</span>
                                </div>
                            </div>
                            
                            <div className="progress-bar-container">
                                <div 
                                    className={`progress-bar ${sendingProgress.completed ? 'completed' : ''}`}
                                    style={{ width: `${(sendingProgress.current / sendingProgress.total) * 100}%` }}
                                />
                            </div>

                            {sendingProgress.completed ? (
                                <div className="completion-message">
                                    {sendingProgress.errorCount === 0 ? (
                                        <div className="completion-success">
                                            ✓ All emails sent successfully!
                                        </div>
                                    ) : (
                                        <div className="completion-warning">
                                            Campaign completed with {sendingProgress.errorCount} error{sendingProgress.errorCount > 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                            ) : sendingProgress.currentEmail && (
                                <div className="current-email">
                                    <span className="current-label">Sending to:</span>
                                    <span className="current-email-value">{sendingProgress.currentEmail}</span>
                                </div>
                            )}

                            {sendingProgress.errors.length > 0 && (
                                <div className="progress-errors">
                                    <strong>Errors:</strong>
                                    <div className="errors-list">
                                        {sendingProgress.errors.map((err, idx) => (
                                            <div key={idx} className="error-item">
                                                <span className="error-email">{err.email}:</span>
                                                <span className="error-message">{err.error}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignManager;
